import { Clock, ZoomIn, ZoomOut } from 'lucide-react';
import { SessionPpgData } from '@repo/shared/dtos/devices/SessionPpgDto';

interface SessionDetailsProps {
  sessionData: SessionPpgData;
  showSession: boolean;
  isZoomed: boolean;
  onToggleSession: () => void;
  onZoom: () => void;
  onResetZoom: () => void;
}

export const SessionDetails = ({
  sessionData,
  showSession,
  isZoomed,
  onToggleSession,
  onZoom,
  onResetZoom,
}: SessionDetailsProps) => {
  const avgHr = sessionData.hrMetrics?.avg;
  const minHr = sessionData.hrMetrics?.min;
  const maxHr = sessionData.hrMetrics?.max;

  const durationMs = new Date(sessionData.endTime).getTime() - new Date(sessionData.startTime).getTime();
  const durationMin = durationMs > 0 ? Math.round(durationMs / 60000) : null;

  return (
    <div className="rounded-lg bg-gradient-to-r from-pink-50 to-purple-50 p-4 shadow-md">
      <div className="mb-2 flex items-center justify-between">
        <h3 className="text-lg font-medium text-gray-700">Session Details</h3>
        <div className="flex space-x-2">
          <button
            onClick={onToggleSession}
            className={`rounded-full px-3 py-1 text-xs transition-colors ${
              showSession ? 'bg-indigo-600 text-white' : 'bg-gray-200 text-gray-600'
            }`}
          >
            {showSession ? 'Hide Session Data' : 'Show Session Data'}
          </button>
          <button
            onClick={isZoomed ? onResetZoom : onZoom}
            className="flex items-center rounded-full bg-indigo-100 px-3 py-1 text-xs text-indigo-700 transition-colors hover:bg-indigo-200"
          >
            {isZoomed ? (
              <>
                <ZoomOut className="mr-1 h-3 w-3" /> View All Data
              </>
            ) : (
              <>
                <ZoomIn className="mr-1 h-3 w-3" /> Focus on Session
              </>
            )}
          </button>
        </div>
      </div>
      <div className="grid grid-cols-4 gap-3">
        <div className="rounded bg-white p-3 shadow-sm">
          <div className="text-sm text-gray-500">Session</div>
          <div className="font-semibold">{sessionData.name}</div>
        </div>
        <div className="rounded bg-white p-3 shadow-sm">
          <div className="text-sm text-gray-500">Duration</div>
          <div className="font-semibold">{durationMin != null ? `${durationMin} min` : 'N/A'}</div>
        </div>
        <div className="rounded bg-white p-3 shadow-sm">
          <div className="text-sm text-gray-500">Avg Heart Rate</div>
          <div className="font-semibold">{avgHr != null ? `${avgHr} BPM` : 'N/A'}</div>
        </div>
        <div className="rounded bg-white p-3 shadow-sm">
          <div className="text-sm text-gray-500">Heart Rate Range</div>
          <div className="font-semibold">
            {minHr != null && maxHr != null ? `${minHr} - ${maxHr} BPM` : 'N/A'}
          </div>
        </div>

        <div className="col-span-4 flex items-center rounded bg-white p-3 shadow-sm">
          <Clock className="mr-2 h-4 w-4 text-indigo-500" />
          <div className="text-sm">
            <span className="text-gray-500">Session Time:</span>{' '}
            <span className="font-medium">
              {new Date(sessionData.startTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              {' - '}
              {new Date(sessionData.endTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};
